import { useEffect, useRef, useState } from "react";
import {
  getHandle,
  hasHandle,
  HandleModal,
  Leaderboard,
  ShareSheet,
  track,
  trackPageView,
  useLeaderboard,
  type SubmitResult,
} from "@karrtopelka/game-kit";
import type { CardProps } from "@karrtopelka/cards/types";

import { Board } from "./components/Board.js";
import { buildReflexCard } from "./lib/buildCard.js";
import { loadCardAssets } from "./lib/cardAssets.js";
import { ROUNDS, useReflexStore } from "./store.js";

export function App() {
  const phase = useReflexStore((s) => s.phase);
  const reset = useReflexStore((s) => s.reset);

  // Warm the card-render assets (2.5MB wasm + fonts) the moment the player starts
  // playing — strong intent, ~30s before they finish — so the share card isn't a cold
  // spinner. Memoized, so this never double-fetches. Skipped for idle bouncers.
  const warmed = useRef(false);
  useEffect(() => {
    if (!warmed.current && phase !== "idle") {
      warmed.current = true;
      void loadCardAssets().catch(() => {});
    }
  }, [phase]);

  // Funnel beacons: one page_view per load, one play_start per session, one
  // play_done per finished run (phase re-enters "done" each run).
  useEffect(() => trackPageView("reflex"), []);
  const started = useRef(false);
  useEffect(() => {
    if (phase === "waiting" && !started.current) {
      started.current = true;
      track("reflex", "play_start");
    }
    if (phase === "done") track("reflex", "play_done");
  }, [phase]);

  // Leaderboard lives at the top level so it's visible on the start screen too — seeing
  // a target is the hook (IDEA.md: see a score → want to beat it). One hook instance
  // feeds both the desktop rail and the mobile section — never double-fetches.
  const board = useLeaderboard("reflex");

  // Handle is browser-persistent; track it in state so the "playing as @x" line and the
  // post flow update immediately after the modal saves it.
  const [handle, setHandleState] = useState<string | null>(() => getHandle());

  return (
    <main
      className="flex min-h-screen min-h-[100dvh] flex-col items-center gap-6 bg-bg px-6 text-text lg:grid lg:grid-cols-[minmax(200px,260px)_minmax(0,44rem)_minmax(240px,300px)] lg:items-start lg:justify-center lg:gap-10 lg:px-10"
      // Safe-area insets so the header clears the notch and content clears the home
      // indicator. min-h-screen is the vh fallback for iOS < 15.4; the dvh override wins
      // where supported (collapses with the mobile URL bar).
      style={{
        paddingTop: "calc(2.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom))",
      }}
    >
      <header
        className="flex animate-fade-up flex-col items-center lg:col-span-3"
        style={{ animationDelay: "0ms" }}
      >
        <h1 className="text-3xl font-extrabold tracking-tight lg:text-5xl">
          reflex
          <span className="animate-breathe text-accent [text-shadow:0_0_18px_rgba(255,111,165,0.55)]">
            .
          </span>
        </h1>
        <p className="text-sm text-text-muted lg:text-base">
          how fast can you react?
        </p>
      </header>

      {/* LEFT rail — quiet reference panels; the game is the star. */}
      <LeftRail />

      {/* CENTER — the game (or the run summary). */}
      <div
        className="flex w-full animate-fade-up flex-col items-center gap-6 lg:min-w-0"
        style={{ animationDelay: "80ms" }}
      >
        {phase === "done" ? (
          <Summary
            board={board}
            handle={handle}
            onHandle={setHandleState}
            onPlayAgain={reset}
          />
        ) : (
          <Board />
        )}

        {/* Mobile-only leaderboard below the board. On the done screen the Summary
            renders its own copy right under the result, so only show this when NOT
            done to avoid duplication. Desktop gets the persistent right rail instead. */}
        {phase !== "done" && <LeaderboardSection board={board} handle={handle} />}
      </div>

      {/* RIGHT rail — persistent leaderboard, visible during play AND results. */}
      <aside
        className="sticky top-8 hidden animate-fade-up lg:flex lg:flex-col lg:gap-3"
        style={{ animationDelay: "240ms" }}
      >
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            best reflex
          </h2>
          <div className="flex flex-col items-start gap-3">
            <Leaderboard
              entries={board.entries}
              loading={board.loading}
              myHandle={handle}
              formatValue={(v) => `${v}ms`}
              range={board.range}
              onRange={board.setRange}
            />
          </div>
        </div>
      </aside>

      <footer
        className="mt-4 flex animate-fade-up flex-col items-center gap-1 text-xs text-text-faint lg:col-span-3 lg:mt-2"
        style={{ animationDelay: "320ms" }}
      >
        {/* mobile: tiny text links, as ever */}
        <div className="flex flex-col items-center gap-1 lg:hidden">
          <a
            href="https://wheel.karrtopelka.com"
            className="hover:text-accent hover:underline"
          >
            also try wheel — push your luck →
          </a>
          <a href="https://karrtopelka.com" className="hover:text-text-muted">
            karrtopelka.com
          </a>
        </div>
        {/* desktop: more-games strip */}
        <div className="hidden items-center gap-3 lg:flex">
          <span className="mr-1 text-[10px] uppercase tracking-wide text-text-faint">
            more games
          </span>
          {SIBLINGS.map((g) => (
            <a
              key={g.name}
              href={g.href}
              className="group flex flex-col items-start rounded-lg border border-border bg-surface/60 px-4 py-2 backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-accent hover:shadow-glow"
            >
              <span className="text-sm font-semibold text-text-muted transition-colors group-hover:text-text">
                {g.name}
              </span>
              <span className="text-[11px] text-text-faint">{g.hook}</span>
            </a>
          ))}
          <a
            href="https://karrtopelka.com"
            className="ml-1 transition-colors hover:text-accent"
          >
            karrtopelka.com →
          </a>
        </div>
      </footer>
    </main>
  );
}

const SIBLINGS = [
  { name: "wheel", hook: "push your luck", href: "https://wheel.karrtopelka.com" },
  { name: "plinko", hook: "drop and pray", href: "https://plinko.karrtopelka.com" },
  { name: "mines", hook: "one wrong tap", href: "https://mines.karrtopelka.com" },
] as const;

/** Desktop-only left rail: how it works, your numbers, keys. Quiet surface cards. */
function LeftRail() {
  const prevBest = useReflexStore((s) => s.prevBest);
  const bestMs = useReflexStore((s) => s.bestMs);
  const lastMs = useReflexStore((s) => s.lastMs);
  const isNewBest = useReflexStore((s) => s.isNewBest);

  const allTime = isNewBest && bestMs !== null ? bestMs : prevBest;

  return (
    <aside
      className="sticky top-8 hidden animate-fade-up lg:flex lg:flex-col lg:gap-4"
      style={{ animationDelay: "160ms" }}
    >
      <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          how it works
        </h2>
        <ul className="flex flex-col gap-1.5 text-sm text-text-muted">
          <li>wait for the pink flash</li>
          <li>tap the instant it hits</li>
          <li>best of {ROUNDS} is your number</li>
        </ul>
      </div>

      <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          you
        </h2>
        <dl className="flex flex-col gap-1.5 text-sm">
          <div className="flex items-baseline justify-between">
            <dt className="text-text-faint">all-time best</dt>
            <dd className="font-display font-bold text-accent">
              {allTime !== null ? `${allTime}ms` : "—"}
            </dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-text-faint">this run</dt>
            <dd className="font-display font-bold text-text">
              {bestMs !== null ? `${bestMs}ms` : "—"}
            </dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-text-faint">last round</dt>
            <dd className="font-display font-bold text-text-muted">
              {lastMs !== null ? `${lastMs}ms` : "—"}
            </dd>
          </div>
        </dl>
      </div>

      <div className="flex items-center gap-2 rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <kbd className="rounded-md border border-border bg-surface-raised px-2 py-0.5 font-mono text-xs text-text-muted">
          space
        </kbd>
        <span className="text-xs text-text-faint">works as a tap</span>
      </div>
    </aside>
  );
}

type Board = ReturnType<typeof useLeaderboard>;

function Summary({
  board,
  handle,
  onHandle,
  onPlayAgain,
}: {
  board: Board;
  handle: string | null;
  onHandle: (h: string) => void;
  onPlayAgain: () => void;
}) {
  const rounds = useReflexStore((s) => s.rounds);
  const bestMs = useReflexStore((s) => s.bestMs);
  const isNewBest = useReflexStore((s) => s.isNewBest);
  const prevBest = useReflexStore((s) => s.prevBest);

  const [showHandle, setShowHandle] = useState(false);
  const [submitted, setSubmitted] = useState<SubmitResult | null>(null);
  const [card, setCard] = useState<CardProps | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);

  const best = bestMs ?? 0;

  const doSubmit = async () => {
    setPosting(true);
    setSubmitError(null);
    const res = await board.submit(best);
    setPosting(false);
    const who = getHandle() ?? "you";
    if (!res) {
      setSubmitError("couldn't submit (rate limited?) — card still works");
      setCard(buildReflexCard(best, who, null));
      return;
    }
    setSubmitted(res);
    setCard(buildReflexCard(best, who, res));
  };

  const onPost = () => {
    if (hasHandle()) void doSubmit();
    else setShowHandle(true);
  };

  return (
    <div className="flex w-full max-w-md flex-col items-center gap-8">
      {/* result */}
      <section
        className="flex animate-fade-up flex-col items-center"
        style={{ animationDelay: "0ms" }}
      >
        {isNewBest && (
          <span className="mb-2 animate-pop rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wide text-bg">
            ⚡ new personal best
          </span>
        )}
        <span
          className={`font-display text-7xl font-extrabold lg:text-8xl ${
            isNewBest ? "animate-glow text-accent" : ""
          }`}
        >
          {best}ms
        </span>
        <span className="mt-1 text-sm text-text-muted">
          best of {ROUNDS}
          {!isNewBest && prevBest !== null && best > prevBest && (
            <> · your best {prevBest}ms</>
          )}
        </span>
        {/* Target to beat, visible without scrolling to the full board below. */}
        {board.entries[0] && board.entries[0].value < best && (
          <span className="mt-2 text-xs text-text-faint">
            #1 is{" "}
            <span className="font-mono text-text-muted">
              {board.entries[0].value}ms
            </span>{" "}
            by {board.entries[0].handle} — {best - board.entries[0].value}ms to go
          </span>
        )}
        {board.entries[0] && board.entries[0].value >= best && (
          <span className="mt-2 text-xs text-accent">👑 you hold #1</span>
        )}
        <ul className="mt-4 flex flex-wrap justify-center gap-2">
          {rounds.map((ms, i) => (
            <li
              key={i}
              className={`rounded-md border px-2.5 py-1 font-display text-sm transition-colors ${
                ms === bestMs
                  ? "border-accent text-accent shadow-glow"
                  : "border-border text-text-muted"
              }`}
            >
              {ms}ms
            </li>
          ))}
        </ul>
      </section>

      {/* submit result line */}
      {submitted && (
        <p className="text-sm text-text">
          #{submitted.rank} of {submitted.total}
          {submitted.total >= 10 && (
            <span className="text-accent"> · top {submitted.percentile}%</span>
          )}
        </p>
      )}
      {submitError && <p className="text-sm text-accent">{submitError}</p>}

      {/* actions / share */}
      {card ? (
        <ShareSheet
          card={card}
          replyLink="reflex.karrtopelka.com/r"
          caption={`${card.display}. ${card.provocation}`}
          loadAssets={loadCardAssets}
        />
      ) : (
        <div
          className="flex w-full animate-fade-up flex-col items-center gap-2"
          style={{ animationDelay: "80ms" }}
        >
          <button
            type="button"
            onClick={onPost}
            disabled={posting}
            className="w-full animate-neon-pulse rounded-md bg-accent px-4 py-3 font-semibold text-bg transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-40"
          >
            {posting ? "posting…" : "post your result"}
          </button>
          {handle && (
            <p className="text-xs text-text-faint">
              posting as <span className="text-text-muted">@{handle}</span>
            </p>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          setSubmitted(null);
          setCard(null);
          setSubmitError(null);
          onPlayAgain();
        }}
        className="text-sm text-text-muted transition-colors hover:text-text"
      >
        play again
      </button>

      <LeaderboardSection board={board} handle={handle} />

      {showHandle && (
        <HandleModal
          onCancel={() => setShowHandle(false)}
          onDone={(h) => {
            setShowHandle(false);
            onHandle(h);
            void doSubmit();
          }}
        />
      )}
    </div>
  );
}

/** Mobile leaderboard block — hidden on lg where the persistent right rail takes over. */
function LeaderboardSection({
  board,
  handle,
}: {
  board: Board;
  handle: string | null;
}) {
  return (
    <section className="flex w-full max-w-md flex-col items-center gap-3 border-t border-border pt-6 lg:hidden">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
        leaderboard
      </h2>
      <Leaderboard
        entries={board.entries}
        loading={board.loading}
        myHandle={handle}
        formatValue={(v) => `${v}ms`}
        range={board.range}
        onRange={board.setRange}
      />
    </section>
  );
}
